<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useAnomalyDetectorStore } from '@/store/anomaly_detector'
import { useRegisterStore } from '@/store/register'
import AnomalyDetectorBoxplot from '@/components/AnomalyDetector/Body/AnomalyDetectorBoxplot.vue'
import AnomalyDetectorComparisonCards from '@/components/AnomalyDetector/Body/AnomalyDetectorComparisonCards.vue'
import RegisterChart from '@/components/Registers/Body/RegisterChart.vue'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { ISensorGroup } from '@/interfaces/stores/anomalyDetector'
import { IStatsSensor, ISensor } from '@/interfaces/entities/sensor'
import type { IEnergySensorReading } from '@/interfaces/stores/register'
import { energyReadingMatchesDateRange, getAnalysisPeriodRange } from '@/utils/date/date-time'

const { t } = useI18n()

const route = useRoute()
const router = useRouter()
const anomalyStore = useAnomalyDetectorStore()
const registerStore = useRegisterStore()

const { getAttentionSensors, getCriticalSensors, getCurrentPeriodFilter } = storeToRefs(anomalyStore)
const { getEnergyReading } = storeToRefs(registerStore)

const sensorId = ref(Number(route.params.sensorId))
const loading = ref(true)
const error = ref<string | null>(null)
const selectedDataField = ref('')

const sensorGroup = ref<ISensorGroup | null>(null)
const sensorType = ref<'attention' | 'critical'>('attention')

/** Tipo do card clicado (query `type`); padrão atenção se ausente ou inválido. */
const cardType = ref<'attention' | 'critical'>(
    route.query.type === 'critical' ? 'critical' : 'attention',
)

function resolveCardTypeFromRoute(): 'attention' | 'critical' {
    return route.query.type === 'critical' ? 'critical' : 'attention'
}

const selectedRow = computed(() => {
    if (!sensorGroup.value || !selectedDataField.value) return null
    return sensorGroup.value.fields.find((f) => f.data_field === selectedDataField.value)
})

const sensorName = computed(() => sensorGroup.value?.sensorName ?? t('anomaly_detector.details.default_sensor_name'))

const analysisPeriod = computed(() => {
    const period = getCurrentPeriodFilter.value
    if (!period) {
        const now = new Date()
        const end = new Date(now)
        const start = new Date(now)
        start.setDate(start.getDate() - 7)
        return getAnalysisPeriodRange([start, end])
    }
    return getAnalysisPeriodRange(period)
})

const sensorReadingData = computed(() => {
    const { dateRange } = analysisPeriod.value
    return getEnergyReading.value.find(
        (reading: IEnergySensorReading) =>
            reading.sensor.id === sensorId.value && energyReadingMatchesDateRange(reading, dateRange),
    )
})

async function loadSensorData() {
    loading.value = true
    error.value = null

    try {
        sensorId.value = Number(route.params.sensorId)
        cardType.value = resolveCardTypeFromRoute()
        sensorType.value = cardType.value

        const sourceSensors =
            cardType.value === 'critical' ? getCriticalSensors.value : getAttentionSensors.value

        const fieldsOfType = sourceSensors.filter(
            (s: IStatsSensor) => (s.sensor?.id ?? s.sensor_id) === sensorId.value,
        )

        if (fieldsOfType.length === 0) {
            error.value = t('anomaly_detector.details.no_field_found')
            loading.value = false
            return
        }

        const group: ISensorGroup = {
            sensorId: sensorId.value,
            sensorName: fieldsOfType[0].sensor?.name ?? '-',
            fields: fieldsOfType,
        }

        sensorGroup.value = group

        if (group.fields.length > 0) {
            selectedDataField.value = group.fields[0].data_field
        }

        const statsSensor = fieldsOfType.find(
            (s: IStatsSensor) => (s.sensor?.id ?? s.sensor_id) === sensorId.value,
        )
        if (!statsSensor?.sensor) {
            error.value = t('anomaly_detector.details.sensor_data_unavailable')
            loading.value = false
            return
        }

        const { dateRange, startDate, endDate } = analysisPeriod.value

        await Promise.all([
            loadSensorReadings(dateRange, statsSensor.sensor as ISensor),
            anomalyStore.fetchFieldStatistics(
                sensorId.value,
                group.fields.map((f) => f.data_field),
                startDate,
                endDate,
            ),
        ])
    } catch (err) {
        error.value = t('anomaly_detector.details.error_loading_sensor')
        console.error(err)
    } finally {
        loading.value = false
    }
}

async function loadSensorReadings(dateRange: string, sensor: ISensor) {
    try {
        await registerStore.fetchSensorReadings({
            sensor,
            dateRange,
        })
    } catch (err) {
        console.error('Erro ao buscar registros do sensor:', err)
        error.value = t('anomaly_detector.details.error_loading_readings')
    }
}

function goBack() {
    router.push({ name: 'anomalyDetector' })
}

onMounted(() => {
    loadSensorData()
})
</script>

<template>
    <DefaultLayout>
        <div class="flex flex-col gap-6 px-2 lg:px-8 py-6">
            <div class="flex items-center gap-4">
                <button
                    @click="goBack"
                    class="px-4 py-2 text-sm text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
                >
                    <i class="pi pi-arrow-left mr-2"></i>
                    {{ t('anomaly_detector.details.back') }}
                </button>
            </div>

            <div v-if="loading" class="flex items-center justify-center py-12">
                <div class="text-gray-500">
                    <i class="pi pi-spinner pi-spin mr-2"></i>
                    {{ t('anomaly_detector.details.loading_data') }}
                </div>
            </div>

            <div v-else-if="error" class="flex flex-col items-center justify-center py-12">
                <div class="text-red-500 mb-4">
                    <i class="pi pi-exclamation-triangle mr-2"></i>
                    {{ error }}
                </div>
                <button
                    @click="goBack"
                    class="px-4 py-2 text-sm text-white bg-red hover:bg-red-600"
                >
                    {{ t('anomaly_detector.details.back_to_list') }}
                </button>
            </div>

            <template v-else-if="sensorGroup">
                <div class="bg-white px-6 py-4 shadow-sm border-l-4" :class="sensorType === 'critical' ? 'border-red-500' : 'border-yellow-500'">
                    <h1 class="text-2xl font-bold text-gray-900">{{ t('anomaly_detector.details.anomaly_report') }}</h1>
                    <p class="text-gray-600 mt-1">{{ sensorName }}</p>
                </div>

                <!-- Campo selecionado -->
                <div v-if="sensorGroup.fields.length > 1" class="bg-white p-2 shadow-sm">
                    <label
                        for="anomaly-detail-field"
                        class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2"
                    >
                        {{ t('anomaly_detector.details.field') }}
                    </label>
                    <select
                        id="anomaly-detail-field"
                        v-model="selectedDataField"
                        class="w-full max-w-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue focus:outline-none"
                    >
                        <option
                            v-for="f in sensorGroup.fields"
                            :key="f.data_field"
                            :value="f.data_field"
                        >
                            {{ f.data_field }}
                        </option>
                    </select>
                </div>

                <!-- Mensagem de anomalia -->
                <div class="flex flex-col gap-6">
                    <div
                        v-if="selectedRow"
                        class="border px-4 py-3 mb-2"
                        :class="sensorType === 'critical' 
                            ? 'border-red-200 bg-red-100' 
                            : 'border-yellow-200 bg-yellow-100'"
                    >
                        <div class="flex items-start gap-2">
                            <i 
                                class="pi text-lg"
                                :class="sensorType === 'critical' 
                                    ? 'pi-exclamation-circle text-red-600' 
                                    : 'pi-exclamation-triangle text-yellow-600'"
                            ></i>
                            <div>
                                <span 
                                    class="font-bold text-sm"
                                    :class="sensorType === 'critical' ? 'text-red-900' : 'text-yellow-900'"
                                >
                                    {{ selectedRow.data_field }}
                                </span>
                                <p 
                                    class="mt-1 text-sm font-normal leading-relaxed"
                                    :class="sensorType === 'critical' ? 'text-red-800' : 'text-yellow-800'"
                                >
                                    {{ selectedRow.message }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <span class="text-lg font-bold text-gray-800 mb-1">{{ t('anomaly_detector.details.statistical_comparison') }}</span>

                    <!-- Layout responsivo: 2 colunas em telas grandes, empilhado em mobile -->
                <div class="grid grid-cols-1 lg:grid-cols-[7fr_3fr] gap-6">
                    <!-- Coluna Esquerda: Boxplot-->
                    <div class="bg-white p-2 shadow-sm flex flex-col">
                        <AnomalyDetectorBoxplot
                            v-if="sensorGroup"
                            :group="sensorGroup"
                            :type="sensorType"
                            v-model:selectedDataField="selectedDataField"
                            height="h-128"
                            :show-title="false"
                        />
                    </div>

                    <!-- Coluna Direita: Cards Comparativos-->
                    <div class="flex flex-col">
                        <AnomalyDetectorComparisonCards
                            v-if="selectedRow"
                            :selectedRow="selectedRow"
                            :type="sensorType"
                        />
                    </div>
                </div>

                <!-- Registros da semana de análise -->
                 <h2 class="text-lg font-bold text-gray-800 mb-1">{{ t('anomaly_detector.details.week_analysis_records') }}</h2>
                <div v-if="sensorReadingData" class="bg-white p-2 shadow-sm mb-1">
                    <RegisterChart
                        :sensorData="sensorReadingData"
                        :cyclePeriods="[]"
                        :sensor-index="sensorReadingData.index"
                    />
                </div>

                <div v-else-if="!loading" class="mt-2 flex items-center justify-center py-12 bg-gray-50">
                    <div class="text-gray-400 text-center">
                        <i class="pi pi-info-circle text-2xl mb-2"></i>
                        <p>{{ t('anomaly_detector.details.no_records_available') }}</p>
                    </div>
                </div>
            </template>
        </div>
    </DefaultLayout>
</template>
